import type { AppDefinition, AppId, Bounds, SnapGroup, VirtualDesktop, WindowState, WorkspaceState } from './types';

const PRIMARY_DESKTOP: VirtualDesktop = { id: 'primary', name: 'Desktop 1' };
const MAX_DESKTOPS = 6;
const desktopIdPattern = /^[a-zA-Z0-9_-]{1,80}$/;

export const defaultWorkspace: WorkspaceState = {
  schemaVersion: 3,
  windows: [],
  activeWindowId: null,
  desktopId: PRIMARY_DESKTOP.id,
  desktops: [PRIMARY_DESKTOP],
  snapGroups: []
};

function validBounds(value: unknown): value is Bounds {
  if (!value || typeof value !== 'object') return false;
  const bounds = value as Bounds;
  return [bounds.x, bounds.y, bounds.width, bounds.height].every(Number.isFinite)
    && bounds.width >= 320 && bounds.height >= 220;
}

export function normalizeWorkspace(input: Partial<WorkspaceState>, validApps: Set<AppId>): WorkspaceState {
  const desktops = normalizeDesktops(input.desktops);
  const desktopIds = new Set(desktops.map(desktop => desktop.id));
  const windows = Array.isArray(input.windows) ? input.windows.filter((candidate): candidate is WindowState => {
    if (!candidate || typeof candidate !== 'object') return false;
    return typeof candidate.id === 'string'
      && validApps.has(candidate.appId)
      && typeof candidate.title === 'string'
      && validBounds(candidate.bounds)
      && typeof candidate.minimized === 'boolean'
      && typeof candidate.maximized === 'boolean'
      && Number.isFinite(candidate.zIndex);
  }).slice(0, 30).map(window => ({
    ...window,
    desktopId: typeof window.desktopId === 'string' && desktopIds.has(window.desktopId) ? window.desktopId : PRIMARY_DESKTOP.id
  })) : [];
  let desktopId = typeof input.desktopId === 'string' && desktopIds.has(input.desktopId) ? input.desktopId : PRIMARY_DESKTOP.id;
  const requestedActive = windows.find(window => window.id === input.activeWindowId);
  if (requestedActive) desktopId = requestedActive.desktopId;
  const activeWindowId = requestedActive && !requestedActive.minimized ? requestedActive.id : topWindow(windows, desktopId)?.id || null;
  const windowMap = new Map(windows.map(window => [window.id, window]));
  const snapGroups = normalizeSnapGroups(input.snapGroups, windowMap);
  return { schemaVersion: 3, windows, activeWindowId, desktopId, desktops, snapGroups };
}

function normalizeSnapGroups(value: unknown, windows: Map<string, WindowState>): SnapGroup[] {
  if (!Array.isArray(value)) return [];
  const groups: SnapGroup[] = [];
  for (const candidate of value) {
    if (!candidate || typeof candidate !== 'object') continue;
    const group = candidate as Partial<SnapGroup>;
    if (typeof group.id !== 'string' || !Array.isArray(group.windowIds) || group.windowIds.length !== 2) continue;
    const first = windows.get(String(group.windowIds[0]));
    const second = windows.get(String(group.windowIds[1]));
    if (!first || !second || first.id === second.id || first.desktopId !== second.desktopId) continue;
    groups.push({ id: group.id.slice(0, 100), desktopId: first.desktopId, windowIds: [first.id, second.id] });
    if (groups.length >= 12) break;
  }
  return groups;
}

function normalizeDesktops(value: unknown): VirtualDesktop[] {
  if (!Array.isArray(value)) return [PRIMARY_DESKTOP];
  const desktops: VirtualDesktop[] = [];
  for (const candidate of value) {
    if (!candidate || typeof candidate !== 'object') continue;
    const desktop = candidate as Partial<VirtualDesktop>;
    if (typeof desktop.id !== 'string' || !desktopIdPattern.test(desktop.id) || desktops.some(item => item.id === desktop.id)) continue;
    const name = typeof desktop.name === 'string' ? desktop.name.trim().slice(0, 40) : '';
    desktops.push({ id: desktop.id, name: name || `Desktop ${desktops.length + 1}` });
    if (desktops.length === MAX_DESKTOPS) break;
  }
  if (!desktops.some(desktop => desktop.id === PRIMARY_DESKTOP.id)) desktops.unshift(PRIMARY_DESKTOP);
  return desktops.slice(0, MAX_DESKTOPS);
}

function topZ(windows: WindowState[]): number {
  return Math.max(10, ...windows.map(window => window.zIndex));
}

function topWindow(windows: WindowState[], desktopId: string, excludedId = ''): WindowState | undefined {
  return [...windows]
    .filter(window => window.desktopId === desktopId && !window.minimized && window.id !== excludedId)
    .sort((a, b) => b.zIndex - a.zIndex)[0];
}

export function launchWindow(state: WorkspaceState, definition: AppDefinition): WorkspaceState {
  const existing = definition.singleton ? state.windows.find(window => window.appId === definition.id) : null;
  if (existing) return focusWindow({
    ...state,
    windows: state.windows.map(window => window.id === existing.id ? { ...window, minimized: false } : window)
  }, existing.id);
  const offset = state.windows.length % 8 * 24;
  const id = crypto.randomUUID();
  const next: WindowState = {
    id,
    appId: definition.id,
    title: definition.name,
    desktopId: state.desktopId,
    bounds: { ...definition.defaultBounds, x: definition.defaultBounds.x + offset, y: definition.defaultBounds.y + offset },
    minimized: false,
    maximized: false,
    zIndex: topZ(state.windows) + 1
  };
  return { ...state, windows: [...state.windows, next], activeWindowId: id };
}

export function focusWindow(state: WorkspaceState, id: string): WorkspaceState {
  const target = state.windows.find(window => window.id === id);
  if (!target) return state;
  if (state.activeWindowId === id && !target.minimized && state.desktopId === target.desktopId) return state;
  const zIndex = topZ(state.windows) + 1;
  return {
    ...state,
    desktopId: target.desktopId,
    activeWindowId: id,
    windows: state.windows.map(window => window.id === id ? { ...window, minimized: false, zIndex } : window)
  };
}

export function closeWindow(state: WorkspaceState, id: string): WorkspaceState {
  const windows = state.windows.filter(window => window.id !== id);
  const next = topWindow(windows, state.desktopId);
  return { ...state, windows, snapGroups: (state.snapGroups || []).filter(group => !group.windowIds.includes(id)), activeWindowId: next?.id || null };
}

export function minimizeWindow(state: WorkspaceState, id: string): WorkspaceState {
  const windows = state.windows.map(window => window.id === id ? { ...window, minimized: true } : window);
  const next = topWindow(windows, state.desktopId, id);
  return { ...state, windows, activeWindowId: next?.id || null };
}

export function maximizeWindow(state: WorkspaceState, id: string): WorkspaceState {
  return {
    ...state,
    activeWindowId: id,
    windows: state.windows.map(window => {
      if (window.id !== id) return window;
      return window.maximized
        ? { ...window, bounds: window.restoreBounds || window.bounds, restoreBounds: undefined, maximized: false }
        : { ...window, restoreBounds: window.bounds, maximized: true, minimized: false };
    })
  };
}

export function moveWindow(state: WorkspaceState, id: string, bounds: Bounds): WorkspaceState {
  return { ...state, windows: state.windows.map(window => window.id === id ? { ...window, bounds } : window) };
}

export function snapWindow(state: WorkspaceState, id: string, bounds: Bounds): WorkspaceState {
  const zIndex = topZ(state.windows) + 1;
  return {
    ...state,
    activeWindowId: id,
    windows: state.windows.map(window => window.id === id
      ? { ...window, bounds, restoreBounds: window.maximized ? window.restoreBounds : undefined, maximized: false, minimized: false, zIndex }
      : window)
  };
}

export function createDesktop(state: WorkspaceState): WorkspaceState {
  if (state.desktops.length >= MAX_DESKTOPS) return state;
  const id = `desktop-${crypto.randomUUID()}`;
  const desktop = { id, name: `Desktop ${state.desktops.length + 1}` };
  return { ...state, desktops: [...state.desktops, desktop], desktopId: id, activeWindowId: null };
}

export function renameDesktop(state: WorkspaceState, id: string, input: string): WorkspaceState {
  const name = input.trim().slice(0, 40);
  if (!name) return state;
  return { ...state, desktops: state.desktops.map(desktop => desktop.id === id ? { ...desktop, name } : desktop) };
}

export function switchDesktop(state: WorkspaceState, id: string): WorkspaceState {
  if (id === state.desktopId || !state.desktops.some(desktop => desktop.id === id)) return state;
  return { ...state, desktopId: id, activeWindowId: topWindow(state.windows, id)?.id || null };
}

export function removeDesktop(state: WorkspaceState, id: string): WorkspaceState {
  if (state.desktops.length === 1 || !state.desktops.some(desktop => desktop.id === id)) return state;
  const index = state.desktops.findIndex(desktop => desktop.id === id);
  const desktops = state.desktops.filter(desktop => desktop.id !== id);
  const fallback = desktops[Math.min(index, desktops.length - 1)];
  const windows = state.windows.map(window => window.desktopId === id ? { ...window, desktopId: fallback.id } : window);
  const desktopId = state.desktopId === id ? fallback.id : state.desktopId;
  return { ...state, desktops, windows, snapGroups: (state.snapGroups || []).map(group => group.desktopId === id ? { ...group, desktopId: fallback.id } : group), desktopId, activeWindowId: topWindow(windows, desktopId)?.id || null };
}

export function moveWindowToDesktop(state: WorkspaceState, windowId: string, desktopId: string): WorkspaceState {
  if (!state.desktops.some(desktop => desktop.id === desktopId)) return state;
  const target = state.windows.find(window => window.id === windowId);
  if (!target || target.desktopId === desktopId) return state;
  const windows = state.windows.map(window => window.id === windowId ? { ...window, desktopId, minimized: false } : window);
  return { ...state, windows, snapGroups: (state.snapGroups || []).filter(group => !group.windowIds.includes(windowId)), activeWindowId: topWindow(windows, state.desktopId, windowId)?.id || null };
}

export function createSnapGroup(state: WorkspaceState, firstId: string, secondId: string): WorkspaceState {
  const first = state.windows.find(window => window.id === firstId);
  const second = state.windows.find(window => window.id === secondId);
  if (!first || !second || first.id === second.id || first.desktopId !== second.desktopId) return state;
  const previous = (state.snapGroups || []).filter(group => !group.windowIds.includes(firstId) && !group.windowIds.includes(secondId));
  const group: SnapGroup = { id: `snap-${crypto.randomUUID()}`, desktopId: first.desktopId, windowIds: [firstId, secondId] };
  return { ...state, snapGroups: [...previous, group].slice(-12) };
}

export function activateSnapGroup(state: WorkspaceState, groupId: string): WorkspaceState {
  const group = (state.snapGroups || []).find(candidate => candidate.id === groupId);
  if (!group) return state;
  let zIndex = topZ(state.windows);
  const windows = state.windows.map(window => group.windowIds.includes(window.id) ? { ...window, minimized: false, zIndex: zIndex += 1 } : window);
  return { ...state, desktopId: group.desktopId, windows, activeWindowId: group.windowIds[1] };
}

export function cycleWindow(state: WorkspaceState, backwards = false): WorkspaceState {
  const windows = state.windows.filter(window => window.desktopId === state.desktopId && !window.minimized).sort((a, b) => b.zIndex - a.zIndex);
  if (windows.length < 2) return windows[0] ? focusWindow(state, windows[0].id) : state;
  const index = Math.max(0, windows.findIndex(window => window.id === state.activeWindowId));
  const offset = backwards ? windows.length - 1 : 1;
  return focusWindow(state, windows[(index + offset) % windows.length].id);
}

export function cycleDesktop(state: WorkspaceState, backwards = false): WorkspaceState {
  if (state.desktops.length < 2) return state;
  const index = Math.max(0, state.desktops.findIndex(desktop => desktop.id === state.desktopId));
  const offset = backwards ? state.desktops.length - 1 : 1;
  return switchDesktop(state, state.desktops[(index + offset) % state.desktops.length].id);
}

export function setDesktopWindowsMinimized(state: WorkspaceState, minimized: boolean, onlyIds?: Set<string>): WorkspaceState {
  const windows = state.windows.map(window => window.desktopId === state.desktopId && (!onlyIds || onlyIds.has(window.id)) ? { ...window, minimized } : window);
  return { ...state, windows, activeWindowId: minimized ? null : topWindow(windows, state.desktopId)?.id || null };
}
